import React, { useState } from "react";
import { X, ArrowRight, Save, Trash2, Train } from "lucide-react";
import { useRouteBuilder } from "../context/RouteContext";
import { useAuth } from "../context/AuthContext";
import { api, fmtDuration } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function RouteBuilder() {
  const { segments, removeSegment, clear } = useRouteBuilder();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const totalDur = segments.reduce((s, x) => s + (x.duration_seconds || 0), 0);
  const totalPrice = segments.reduce((s, x) => s + (x.est_price_eur || 0), 0);
  const cities = [...new Set(segments.flatMap((s) => [s.from_city, s.to_city]).filter(Boolean))];

  const handleSave = async () => {
    if (!user) {
      navigate("/login?next=/search");
      return;
    }
    if (!name.trim() || segments.length === 0) return;
    setSaving(true);
    try {
      // Derive city slugs by matching to catalog client-side (fetch lazily)
      const { data: catalog } = await api.get("/cities");
      const slugMatches = cities
        .map((c) => {
          const m = catalog.find((cc) => c.toLowerCase().includes(cc.name.toLowerCase()));
          return m ? m.slug : null;
        })
        .filter(Boolean);
      const payload = {
        name: name.trim(),
        segments: segments.map(({ _localId, legs, ...rest }) => rest),
        cities: [...new Set(slugMatches)],
      };
      await api.post("/routes", payload);
      await refreshUser();
      toast.success("Route saved to your passport.");
      clear();
      setName("");
    } catch (e) {
      toast.error("Could not save route.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside
      data-testid="route-builder"
      className="iq-card sticky top-24 max-h-[calc(100vh-7rem)] flex flex-col overflow-hidden"
    >
      <div className="px-5 py-4 border-b border-panel-border flex items-center justify-between">
        <div>
          <div className="iq-overline">Your route</div>
          <h3 className="font-display text-2xl text-cream tracking-wider">ITINERARY</h3>
        </div>
        {segments.length > 0 && (
          <button data-testid="route-clear-btn" onClick={clear} className="iq-btn-ghost text-xs" title="Clear all">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {segments.length === 0 && (
          <div className="text-center py-12 px-4">
            <Train className="h-10 w-10 text-gold/40 mx-auto mb-3" />
            <p className="text-cream-dim text-sm leading-relaxed font-serif italic">
              Your odyssey begins with a single departure. Add itineraries from the search results to build your route.
            </p>
          </div>
        )}
        {segments.map((s, i) => (
          <div
            key={s._localId}
            data-testid={`route-segment-${i}`}
            className="bg-ink border border-panel-border rounded-sm p-3 group hover:border-gold/40 transition-colors"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-display tracking-wide text-cream truncate">
                <span className="truncate">{s.from_city}</span>
                <ArrowRight className="h-3.5 w-3.5 text-gold shrink-0" />
                <span className="truncate">{s.to_city}</span>
              </div>
              <button
                data-testid={`route-remove-${i}`}
                onClick={() => removeSegment(s._localId)}
                className="text-cream-dim/60 hover:text-accent-red transition-colors opacity-0 group-hover:opacity-100"
                aria-label="Remove segment"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center justify-between mt-1.5 text-[11px] text-cream-dim/70">
              <span>{fmtDuration(s.duration_seconds)} · {s.transfers || 0} changes</span>
              {s.est_price_eur != null && <span className="text-gold">€{Math.round(s.est_price_eur)}</span>}
            </div>
          </div>
        ))}
      </div>

      {segments.length > 0 && (
        <div className="border-t border-panel-border p-4 space-y-3 bg-ink/30">
          <div className="flex items-center justify-between">
            <span className="text-cream-dim text-sm">Total time</span>
            <span className="font-display text-cream text-lg">{fmtDuration(totalDur)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-cream-dim text-sm">Est. cost</span>
            <span className="font-display text-gold text-xl">€{Math.round(totalPrice)}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-cream-dim/70 pb-2">
            <span>{cities.length} cities</span>
            <span>{segments.length} legs</span>
          </div>
          <input
            data-testid="route-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name your route…"
            className="iq-input"
          />
          <button
            data-testid="route-save-btn"
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className="iq-btn-primary w-full text-sm py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            {user ? (saving ? "Saving…" : "Save route") : "Sign in to save"}
          </button>
          <p className="text-[10px] text-cream-dim/50 text-center italic leading-relaxed">
            Booking is coming soon. Prices are indicative estimates.
          </p>
        </div>
      )}
    </aside>
  );
}
